import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { supabase } from '../lib/supabase';
import {
  Menu,
  X,
  LogOut,
  CreditCard,
  Settings,
  LayoutDashboard,
  ChevronDown,
  HelpCircle,
  User,
  Shield,
  Users,
  Share2,
  Crown,
  ArrowUpRight,
  Save,
  Gift,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';

const Navbar = () => {
  const { user, signOut, subscription } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const dropdownRef = useRef(null);

  const hasActiveSubscription = subscription?.hasSubscription;

  // Zavřít dropdown při kliknutí mimo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsDropdownOpen(false);
  };

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.charAt(0).toUpperCase();
  };

  const getUserName = () => {
    if (!user?.email) return 'Uživatel';
    return user.email.split('@')[0];
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="/images/logo.png"
                alt="Synapso"
                className="w-12 h-12 rounded-xl object-contain"
              />
              <span className="text-xl font-bold text-white">Synapso</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/jak-to-funguje" className="text-dark-300 hover:text-white transition-colors">
                Jak to funguje
              </Link>
              <Link to="/pricing" className="text-dark-300 hover:text-white transition-colors">
                Předplatné
              </Link>
              <Link to="/affiliate" className="text-dark-300 hover:text-white transition-colors">
                Affiliate
              </Link>
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  {/* Pozvi kamaráda tlačítko */}
                  <button
                    onClick={() => setShowReferralModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium hover:opacity-90 transition-opacity"
                  >
                    <Gift size={18} />
                    <span>Pozvi kamaráda</span>
                  </button>

                  <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-3 px-3 py-2 rounded-xl hover:bg-dark-800 transition-colors"
                  >
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">{getUserInitials()}</span>
                    </div>
                    <div className="text-left hidden lg:block">
                      <p className="text-white text-sm font-medium">{getUserName()}</p>
                      <p className="text-dark-400 text-xs">
                        {hasActiveSubscription ? 'Premium' : 'Free'}
                      </p>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`text-dark-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Dropdown Menu - pouze 5 položek */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 glass rounded-xl border border-dark-700 shadow-xl overflow-hidden animate-fadeIn">
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-dark-700">
                        <p className="text-white font-medium">{getUserName()}</p>
                        <p className="text-dark-400 text-sm truncate">{user.email}</p>
                      </div>

                      {/* Menu Items - v pořadí: Předplatné, Nastavení, Dashboard, Nápověda, Odhlásit */}
                      <div className="py-2">
                        <Link
                          to="/pricing"
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2.5 text-dark-300 hover:text-white hover:bg-dark-800 transition-colors"
                        >
                          <CreditCard size={18} />
                          <span>Předplatné</span>
                          {hasActiveSubscription && (
                            <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">
                              Aktivní
                            </span>
                          )}
                        </Link>

                        <button
                          onClick={() => {
                            setShowSettingsModal(true);
                            setIsDropdownOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-2.5 text-dark-300 hover:text-white hover:bg-dark-800 transition-colors"
                        >
                          <Settings size={18} />
                          <span>Nastavení</span>
                        </button>

                        <Link
                          to="/dashboard"
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2.5 text-dark-300 hover:text-white hover:bg-dark-800 transition-colors"
                        >
                          <LayoutDashboard size={18} />
                          <span>Dashboard</span>
                        </Link>

                        <Link
                          to="/jak-to-funguje"
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2.5 text-dark-300 hover:text-white hover:bg-dark-800 transition-colors"
                        >
                          <HelpCircle size={18} />
                          <span>Nápověda</span>
                        </Link>

                        <div className="border-t border-dark-700 my-2"></div>

                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center space-x-3 px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        >
                          <LogOut size={18} />
                          <span>Odhlásit se</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-dark-300 hover:text-white transition-colors"
                  >
                    Přihlásit
                  </Link>
                  <Link
                    to="/register"
                    className="px-6 py-2 rounded-lg gradient-bg text-white font-semibold btn-hover"
                  >
                    Registrace
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden glass border-t border-dark-700">
            <div className="px-4 py-4 space-y-4">
              <Link
                to="/jak-to-funguje"
                className="block text-dark-300 hover:text-white transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Jak to funguje
              </Link>
              <Link
                to="/pricing"
                className="block text-dark-300 hover:text-white transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Předplatné
              </Link>
              <Link
                to="/affiliate"
                className="block text-dark-300 hover:text-white transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Affiliate
              </Link>
              <div className="pt-4 border-t border-dark-700 space-y-4">
                {user ? (
                  <>
                    {/* User info */}
                    <div className="flex items-center space-x-3 pb-4 border-b border-dark-700">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                        <span className="text-white font-semibold">{getUserInitials()}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{getUserName()}</p>
                        <p className="text-dark-400 text-sm">{user.email}</p>
                      </div>
                    </div>

                    <Link
                      to="/pricing"
                      className="flex items-center space-x-2 text-dark-300 hover:text-white"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <CreditCard size={20} />
                      <span>Předplatné</span>
                    </Link>

                    <button
                      onClick={() => {
                        setShowSettingsModal(true);
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-2 text-dark-300 hover:text-white"
                    >
                      <Settings size={20} />
                      <span>Nastavení</span>
                    </button>

                    <Link
                      to="/dashboard"
                      className="flex items-center space-x-2 text-dark-300 hover:text-white"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LayoutDashboard size={20} />
                      <span>Dashboard</span>
                    </Link>

                    <Link
                      to="/jak-to-funguje"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center space-x-2 text-dark-300 hover:text-white"
                    >
                      <HelpCircle size={20} />
                      <span>Nápověda</span>
                    </Link>

                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-2 text-red-400 hover:text-red-300"
                    >
                      <LogOut size={20} />
                      <span>Odhlásit se</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block text-dark-300 hover:text-white"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Přihlásit
                    </Link>
                    <Link
                      to="/register"
                      className="block text-center px-6 py-2 rounded-lg gradient-bg text-white font-semibold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Registrace
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          user={user}
          subscription={subscription}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      {/* Referral Modal */}
      {showReferralModal && (
        <ReferralModal
          user={user}
          onClose={() => setShowReferralModal(false)}
        />
      )}
    </>
  );
};

// Rozšířený Settings Modal s bočním menu
const SettingsModal = ({ user, subscription, onClose }) => {
  const { updatePassword } = useAuth();
  const [activeSection, setActiveSection] = useState('account');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  // Profilové údaje pro fakturaci
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    ico: '',
    dic: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

  const hasActiveSubscription = subscription?.hasSubscription;
  const currentPlan = subscription?.subscription?.plan || null;

  // Načíst profilová data při otevření
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (data) {
          setProfileData({
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            address: data.address || '',
            ico: data.ico || '',
            dic: data.dic || ''
          });
        }
      } catch (error) {
        // Profil neexistuje, použijeme výchozí hodnoty
      }
    };
    loadProfile();
  }, [user?.id]);

  const handleProfileSave = async () => {
    if (!user?.id) return;

    setProfileLoading(true);
    setProfileMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          address: profileData.address,
          ico: profileData.ico,
          dic: profileData.dic,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;
      setProfileMessage({ type: 'success', text: 'Údaje byly úspěšně uloženy' });
    } catch (error) {
      console.error('Error saving profile:', error);
      setProfileMessage({ type: 'error', text: 'Chyba při ukládání údajů' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Hesla se neshodují' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Heslo musí mít alespoň 6 znaků' });
      return;
    }

    setLoading(true);
    try {
      await updatePassword(newPassword);
      setMessage({ type: 'success', text: 'Heslo bylo úspěšně změněno' });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Chyba při změně hesla' });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    const customerId = subscription?.subscription?.stripe_customer_id;

    if (customerId) {
      try {
        const { url } = await api.createPortalSession(customerId);
        if (url) {
          window.location.href = url;
        }
      } catch (error) {
        console.error('Error creating portal session:', error);
        // Fallback na pricing stránku při chybě
        window.location.href = '/pricing';
      }
    } else {
      // Pokud není Stripe customer ID (testovací předplatné), přesměruj na pricing
      window.location.href = '/pricing';
    }
  };

  const menuItems = [
    { id: 'account', label: 'Účet a fakturace', icon: User },
    { id: 'billing', label: 'Předplatné', icon: CreditCard },
    { id: 'referral', label: 'Pozvi kamaráda', icon: Users },
    { id: 'affiliate', label: 'Affiliate', icon: Share2 },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'security', label: 'Zabezpečení', icon: Shield },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
      <div className="glass rounded-2xl w-full max-w-4xl h-[600px] max-h-[90vh] overflow-hidden animate-fadeIn flex">
        {/* Sidebar */}
        <div className="w-56 border-r border-dark-700 flex flex-col">
          <div className="p-4 border-b border-dark-700">
            <h2 className="text-lg font-bold text-white">Nastavení</h2>
          </div>
          <nav className="flex-1 py-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                  activeSection === item.id
                    ? 'bg-primary-500/20 text-primary-400 border-r-2 border-primary-400'
                    : 'text-dark-300 hover:text-white hover:bg-dark-800'
                }`}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-dark-700">
            <h3 className="text-lg font-semibold text-white">
              {menuItems.find(item => item.id === activeSection)?.label}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
            >
              <X size={20} className="text-dark-400" />
            </button>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Účet a fakturace */}
            {activeSection === 'account' && (
              <div className="space-y-6">
                <p className="text-dark-400">Spravujte své osobní údaje a fakturační informace.</p>

                {/* Avatar a jméno */}
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">
                      {profileData.firstName ? profileData.firstName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium text-lg">
                      {profileData.firstName && profileData.lastName
                        ? `${profileData.firstName} ${profileData.lastName}`
                        : user?.email?.split('@')[0]}
                    </p>
                    <p className="text-dark-400">{user?.email}</p>
                  </div>
                </div>

                {/* Osobní údaje */}
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Osobní údaje</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">Jméno</label>
                      <input
                        type="text"
                        value={profileData.firstName}
                        onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                        placeholder="Zadejte jméno"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">Příjmení</label>
                      <input
                        type="text"
                        value={profileData.lastName}
                        onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                        placeholder="Zadejte příjmení"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="glass rounded-xl p-4">
                    <label className="block text-dark-400 text-sm mb-1">Email</label>
                    <p className="text-white">{user?.email}</p>
                  </div>
                </div>

                {/* Fakturační údaje */}
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Fakturační údaje</h4>

                  <div>
                    <label className="block text-dark-400 text-sm mb-2">Adresa</label>
                    <input
                      type="text"
                      value={profileData.address}
                      onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                      placeholder="Ulice, město, PSČ"
                      className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">IČO</label>
                      <input
                        type="text"
                        value={profileData.ico}
                        onChange={(e) => setProfileData({ ...profileData, ico: e.target.value })}
                        placeholder="12345678"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">DIČ</label>
                      <input
                        type="text"
                        value={profileData.dic}
                        onChange={(e) => setProfileData({ ...profileData, dic: e.target.value })}
                        placeholder="CZ12345678"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Zpráva o uložení */}
                {profileMessage.text && (
                  <div className={`p-3 rounded-lg text-sm ${
                    profileMessage.type === 'error'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-green-500/20 text-green-400'
                  }`}>
                    {profileMessage.text}
                  </div>
                )}

                {/* Tlačítko uložit */}
                <button
                  onClick={handleProfileSave}
                  disabled={profileLoading}
                  className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  <Save size={18} />
                  <span>{profileLoading ? 'Ukládám...' : 'Uložit změny'}</span>
                </button>

                {/* Další info */}
                <div className="pt-4 border-t border-dark-700 space-y-4">
                  <div className="glass rounded-xl p-4">
                    <label className="block text-dark-400 text-sm mb-1">ID účtu</label>
                    <p className="text-white font-mono text-sm">{user?.id}</p>
                  </div>
                  <div className="glass rounded-xl p-4">
                    <label className="block text-dark-400 text-sm mb-1">Datum registrace</label>
                    <p className="text-white">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString('cs-CZ') : '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Předplatné */}
            {activeSection === 'billing' && (
              <div className="space-y-6">
                <p className="text-dark-400">Spravujte své předplatné a platební údaje.</p>

                {/* Aktuální plán */}
                <div className="glass rounded-xl p-6 border border-primary-500/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasActiveSubscription ? 'bg-primary-500/20' : 'bg-dark-700'}`}>
                        <Crown size={20} className={hasActiveSubscription ? 'text-primary-400' : 'text-dark-400'} />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Aktuální plán</p>
                        <p className="text-dark-400 text-sm">
                          {hasActiveSubscription ? subscription?.subscription?.plan?.replace('_', ' ') || 'Premium' : 'Žádné aktivní předplatné'}
                        </p>
                      </div>
                    </div>
                    {hasActiveSubscription && (
                      <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                        Aktivní
                      </span>
                    )}
                  </div>

                  {hasActiveSubscription && subscription?.subscription?.expires_at && (
                    <p className="text-dark-400 text-sm mb-4">
                      Platné do: {new Date(subscription.subscription.expires_at).toLocaleDateString('cs-CZ')}
                    </p>
                  )}

                  {hasActiveSubscription ? (
                    <button
                      onClick={handleManageSubscription}
                      className="w-full px-4 py-2 rounded-xl bg-dark-700 text-white font-medium hover:bg-dark-600 transition-colors"
                    >
                      Spravovat předplatné
                    </button>
                  ) : (
                    <Link
                      to="/pricing"
                      onClick={onClose}
                      className="block w-full px-4 py-2 rounded-xl bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors text-center"
                    >
                      Aktivovat předplatné
                    </Link>
                  )}
                </div>

                {/* Dostupné plány */}
                <div>
                  <h4 className="text-white font-medium mb-4">Dostupné plány</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {/* 1 měsíc */}
                    <div className={`glass rounded-xl p-4 border ${currentPlan === '1_month' ? 'border-green-500/50' : 'border-dark-600'}`}>
                      <p className="text-dark-400 text-sm mb-1">1 měsíc</p>
                      <p className="text-white text-xl font-bold mb-1">249 Kč</p>
                      <p className="text-dark-500 text-xs mb-3">249 Kč/měsíc</p>
                      {currentPlan === '1_month' ? (
                        <span className="block w-full px-3 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm text-center">
                          Aktuální
                        </span>
                      ) : (
                        <Link
                          to="/pricing"
                          onClick={onClose}
                          className="block w-full px-3 py-2 rounded-lg bg-dark-700 text-dark-300 text-sm text-center hover:bg-dark-600 transition-colors"
                        >
                          Vybrat
                        </Link>
                      )}
                    </div>

                    {/* 3 měsíce */}
                    <div className={`glass rounded-xl p-4 border ${currentPlan === '3_months' ? 'border-green-500/50' : 'border-dark-600'}`}>
                      <p className="text-dark-400 text-sm mb-1">3 měsíce</p>
                      <p className="text-white text-xl font-bold mb-1">699 Kč</p>
                      <p className="text-dark-500 text-xs mb-3">233 Kč/měsíc</p>
                      {currentPlan === '3_months' ? (
                        <span className="block w-full px-3 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm text-center">
                          Aktuální
                        </span>
                      ) : (
                        <Link
                          to="/pricing"
                          onClick={onClose}
                          className="block w-full px-3 py-2 rounded-lg bg-dark-700 text-dark-300 text-sm text-center hover:bg-dark-600 transition-colors"
                        >
                          Vybrat
                        </Link>
                      )}
                    </div>

                    {/* 6 měsíců */}
                    <div className={`glass rounded-xl p-4 border relative ${currentPlan === '6_months' ? 'border-green-500/50' : 'border-primary-500/50'}`}>
                      {currentPlan !== '6_months' && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 text-xs rounded-full bg-primary-500 text-white">
                          Nejlepší volba
                        </span>
                      )}
                      <p className={`text-sm mb-1 ${currentPlan === '6_months' ? 'text-green-400' : 'text-primary-400'}`}>6 měsíců</p>
                      <p className="text-white text-xl font-bold mb-1">1 290 Kč</p>
                      <p className="text-dark-500 text-xs mb-3">215 Kč/měsíc</p>
                      {currentPlan === '6_months' ? (
                        <span className="block w-full px-3 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm text-center">
                          Aktuální
                        </span>
                      ) : (
                        <Link
                          to="/pricing"
                          onClick={onClose}
                          className="block w-full px-3 py-2 rounded-lg bg-primary-500 text-white text-sm text-center hover:bg-primary-600 transition-colors"
                        >
                          Vybrat
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pozvi kamaráda */}
            {activeSection === 'referral' && (
              <ReferralSection user={user} />
            )}

            {/* Affiliate */}
            {activeSection === 'affiliate' && (
              <AffiliateSection user={user} onClose={onClose} />
            )}

            {/* Dashboard odkaz */}
            {activeSection === 'dashboard' && (
              <div className="space-y-6">
                <p className="text-dark-400">Přejděte na svůj dashboard pro přehled výsledků a statistik.</p>

                <Link
                  to="/dashboard"
                  onClick={onClose}
                  className="glass rounded-xl p-6 flex items-center justify-between hover:bg-dark-800 transition-colors group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <LayoutDashboard size={24} className="text-primary-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Přejít na Dashboard</p>
                      <p className="text-dark-400 text-sm">Vaše výsledky, statistiky a oblíbené</p>
                    </div>
                  </div>
                  <ArrowUpRight size={20} className="text-dark-400 group-hover:text-primary-400 transition-colors" />
                </Link>
              </div>
            )}

            {/* Zabezpečení */}
            {activeSection === 'security' && (
              <div className="space-y-6">
                <p className="text-dark-400">Změňte své heslo a zabezpečte svůj účet.</p>

                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-dark-400 text-sm mb-2">Nové heslo</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Zadejte nové heslo"
                      className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-dark-400 text-sm mb-2">Potvrdit nové heslo</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Potvrďte nové heslo"
                      className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  {message.text && (
                    <div className={`p-3 rounded-lg text-sm ${
                      message.type === 'error'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {message.text}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !newPassword || !confirmPassword}
                    className="w-full px-4 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Ukládám...' : 'Změnit heslo'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Referral Section pro Settings Modal (s novým systémem procentuálních slev)
const ReferralSection = ({ user }) => {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0,
    pendingReferrals: 0,
    currentDiscount: 0
  });

  useEffect(() => {
    const loadReferralData = async () => {
      if (!user?.id) return;
      try {
        const [codeRes, statsRes] = await Promise.all([
          api.getReferralCode(user.id),
          api.getReferralStats(user.id)
        ]);
        setReferralCode(codeRes.code || '');
        setStats({
          totalReferrals: statsRes.totalReferrals || 0,
          activeReferrals: statsRes.activeReferrals || 0,
          pendingReferrals: statsRes.pendingReferrals || 0,
          currentDiscount: statsRes.currentDiscount || 0
        });
      } catch (error) {
        console.log('Error loading referral data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadReferralData();
  }, [user?.id]);

  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getDiscountTier = (activeCount) => {
    if (activeCount >= 11) return { percent: 25, next: null, remaining: 0 };
    if (activeCount >= 6) return { percent: 20, next: 25, remaining: 11 - activeCount };
    if (activeCount >= 1) return { percent: 15, next: 20, remaining: 6 - activeCount };
    return { percent: 0, next: 15, remaining: 1 };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const tier = getDiscountTier(stats.activeReferrals);

  return (
    <div className="space-y-6">
      <p className="text-dark-400">Pozvi kamarády a získej slevu na předplatné.</p>

      {/* Aktuální sleva */}
      <div className="glass rounded-xl p-4 border border-primary-500/30">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white font-medium">Tvoje aktuální sleva</span>
          <span className="text-2xl font-bold text-primary-400">{tier.percent}%</span>
        </div>

        {/* Progress bar */}
        <div className="relative h-2 bg-dark-700 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-purple-500 rounded-full transition-all duration-500"
            style={{ width: `${Math.min((stats.activeReferrals / 11) * 100, 100)}%` }}
          />
        </div>

        <div className="flex justify-between text-xs text-dark-400 mb-3">
          <span className={stats.activeReferrals >= 1 ? 'text-primary-400' : ''}>1-5: 15%</span>
          <span className={stats.activeReferrals >= 6 ? 'text-primary-400' : ''}>6-10: 20%</span>
          <span className={stats.activeReferrals >= 11 ? 'text-primary-400' : ''}>11+: 25%</span>
        </div>

        {tier.next && (
          <p className="text-dark-400 text-sm">
            Ještě <span className="text-white font-medium">{tier.remaining}</span> aktivních pozvání do {tier.next}% slevy
          </p>
        )}
      </div>

      {/* Statistiky */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{stats.totalReferrals}</p>
          <p className="text-dark-400 text-sm">Celkem pozváno</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{stats.activeReferrals}</p>
          <p className="text-dark-400 text-sm">S předplatným</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-amber-400">{stats.pendingReferrals}</p>
          <p className="text-dark-400 text-sm">Čekající</p>
        </div>
      </div>

      {/* Referral kód */}
      <div>
        <label className="block text-dark-400 text-sm mb-2">Tvůj referral kód</label>
        <div className="flex items-center space-x-2">
          <div className="flex-1 px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 font-mono text-lg text-white text-center tracking-wider">
            {referralCode}
          </div>
          <button
            onClick={() => handleCopy(referralCode)}
            className="p-3 rounded-xl bg-dark-700 hover:bg-dark-600 transition-colors"
          >
            {copied ? <Check size={20} className="text-green-400" /> : <Copy size={20} className="text-dark-300" />}
          </button>
        </div>
      </div>

      {/* Referral odkaz */}
      <div>
        <label className="block text-dark-400 text-sm mb-2">Referral odkaz</label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="flex-1 px-4 py-2.5 rounded-xl bg-dark-800 text-white border border-dark-700 text-sm"
          />
          <button
            onClick={() => handleCopy(referralLink)}
            className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center space-x-2 ${
              copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            <span>{copied ? 'Zkopírováno' : 'Kopírovat'}</span>
          </button>
        </div>
      </div>

      {/* Jak to funguje */}
      <div className="pt-4 border-t border-dark-700">
        <h4 className="text-white font-medium mb-3">Jak to funguje?</h4>
        <div className="space-y-2 text-sm text-dark-400">
          <p>• Pozvaný kamarád dostane <span className="text-white">10% slevu</span> na první předplatné</p>
          <p>• Za každého kamaráda s aktivním předplatným dostáváš slevu <span className="text-white">15-25%</span></p>
          <p>• Čím více aktivních pozvaných, tím vyšší tvoje sleva</p>
        </div>
      </div>
    </div>
  );
};

// Referral Modal - "Pozvi kamaráda a získej slevu"
const ReferralModal = ({ user, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [stats, setStats] = useState({ activeReferrals: 0 });

  useEffect(() => {
    const loadReferralData = async () => {
      if (!user?.id) return;
      try {
        const [codeRes, statsRes] = await Promise.all([
          api.getReferralCode(user.id),
          api.getReferralStats(user.id)
        ]);
        setReferralCode(codeRes.code || '');
        setStats({ activeReferrals: statsRes.activeReferrals || 0 });
      } catch (error) {
        console.log('Error loading referral data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadReferralData();
  }, [user?.id]);

  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getDiscountTier = (activeCount) => {
    if (activeCount >= 11) return 25;
    if (activeCount >= 6) return 20;
    if (activeCount >= 1) return 15;
    return 0;
  };

  const currentDiscount = getDiscountTier(stats.activeReferrals);

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
      <div className="glass rounded-2xl w-full max-w-md overflow-hidden animate-fadeIn relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-dark-700 rounded-lg transition-colors z-10"
        >
          <X size={20} className="text-dark-400" />
        </button>

        <div className="p-6 text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
            <Gift size={32} className="text-white" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-2">Pozvi a získej slevu</h2>
          <p className="text-dark-400 mb-4">Pozvi kamarády a ušetři na předplatném!</p>

          {/* Current discount badge */}
          {currentDiscount > 0 && (
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 mb-4">
              <span className="text-green-400 font-semibold">🎉 Tvoje aktuální sleva: {currentDiscount}%</span>
            </div>
          )}

          {/* Benefits */}
          <div className="glass rounded-xl p-4 mb-6 text-left">
            <h3 className="text-white font-semibold mb-3">Co získáš?</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-primary-400">•</span>
                <span className="text-dark-300">Kamarád dostane <span className="text-white font-medium">10% slevu</span> na první předplatné</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-primary-400">•</span>
                <span className="text-dark-300">Ty získáš slevu <span className="text-white font-medium">15-25%</span> podle počtu aktivních pozvaných</span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-dark-700">
              <div className="flex justify-between text-xs text-dark-400">
                <span>1-5 pozvaných: 15%</span>
                <span>6-10: 20%</span>
                <span>11+: 25%</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <>
              {/* Referral code */}
              <div className="mb-4">
                <label className="block text-dark-400 text-sm mb-2 text-left">Tvůj referral kód:</label>
                <div className="px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 font-mono text-lg text-white tracking-wider">
                  {referralCode}
                </div>
              </div>

              {/* Referral link */}
              <div className="mb-4">
                <label className="block text-dark-400 text-sm mb-2 text-left">Nebo sdílej odkaz:</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 text-sm"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-4 py-3 rounded-xl font-medium transition-all ${
                      copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}
                  >
                    {copied ? <Check size={20} /> : <Copy size={20} />}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Terms */}
          <p className="text-dark-500 text-xs">
            Sleva se aplikuje automaticky při dalším předplatném.
          </p>
        </div>
      </div>
    </div>
  );
};

// Affiliate Section pro Settings Modal
const AffiliateSection = ({ user, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [stats, setStats] = useState({
    clicks: 0,
    signups: 0,
    conversions: 0,
    earned: 0,
    pendingBalance: 0
  });

  useEffect(() => {
    const loadAffiliateData = async () => {
      if (!user?.id) return;
      try {
        const codeRes = await api.getReferralCode(user.id);
        const statsRes = await api.getReferralStats(user.id);

        setAffiliateCode(codeRes.code || '');
        setStats({
          clicks: 0,
          signups: statsRes.totalReferrals || 0,
          conversions: statsRes.activeReferrals || 0,
          earned: (statsRes.activeReferrals || 0) * 50,
          pendingBalance: 0
        });
      } catch (error) {
        console.log('Error loading affiliate data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAffiliateData();
  }, [user?.id]);

  const affiliateLink = `https://synapso.cz/?ref=${affiliateCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-dark-400">Získej 20% provizi z každého předplatného</p>

      {/* Affiliate Link */}
      <div>
        <label className="block text-dark-400 text-sm mb-2">Tvůj affiliate odkaz</label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={affiliateLink}
            readOnly
            className="flex-1 px-4 py-2.5 rounded-xl bg-dark-800 text-white border border-dark-700 text-sm"
          />
          <button
            onClick={handleCopy}
            className={`px-4 py-2.5 rounded-xl font-medium transition-all ${
              copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
        </div>
        <p className="text-dark-500 text-xs mt-2">
          Tvůj kód: <span className="text-primary-400 font-mono">{affiliateCode}</span>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-white">{stats.clicks}</p>
          <p className="text-dark-500 text-xs">Kliknutí</p>
        </div>
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-white">{stats.signups}</p>
          <p className="text-dark-500 text-xs">Registrací</p>
        </div>
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-white">{stats.conversions}</p>
          <p className="text-dark-500 text-xs">Konverzí</p>
        </div>
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-white">{stats.earned.toFixed(0)}</p>
          <p className="text-dark-500 text-xs">Kč</p>
        </div>
      </div>

      {/* Pending Balance */}
      <div className="glass rounded-xl p-4">
        <h4 className="text-white font-medium mb-1">Nevyplacený zůstatek</h4>
        <p className="text-2xl font-bold text-white">{stats.pendingBalance.toFixed(2)} Kč</p>
        <p className="text-dark-500 text-xs mt-1">K vyplacení: {stats.availableForPayout?.toFixed(2) || 0} Kč</p>
      </div>

      {/* Affiliate Dashboard Link */}
      <Link
        to="/affiliate/dashboard"
        onClick={onClose}
        className="block w-full px-4 py-3 rounded-xl bg-primary-500 text-white font-medium text-center hover:bg-primary-600 transition-colors"
      >
        Přejít na dashboard
      </Link>
    </div>
  );
};

export default Navbar;
